import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AutoAwesomeRounded, PersonRemoveRounded } from "@mui/icons-material";

import { Popper, Table } from "@fragments";

import FiltersBar from "./components/FiltersBar";
import MemberCell from "./components/MemberCell";
import RoleCell from "./components/RoleCell";
import SubscriptionCell from "./components/SubscriptionCell";
import MemberSinceCell from "./components/MemberSinceCell";
import RowActions from "./components/RowActions";
import TableToolbar from "./components/TableToolbar";

import "./styles.scss";

export default function MembersListView({
  open,
  ModalComponent,
  onClose,
  loading,
  members,
  page,
  pages,
  records,
  filters,
  plans,
  plansLoading,
  communityId,
  handleSearch,
  toggleFilterValue,
  handlePageChange,
  clearFilters,
  onActivateSubscription,
  onChangeRole,
  onRemoveMember,
  onExport,
}) {
  const navigate = useNavigate();
  const rowMenuPopperRef = useRef(null);

  function handleViewMember(member) {
    if (!member?.user_id) return;
    onClose();
    navigate(`/user/${member.user_id}`);
  }

  function openRowMenu(member, event) {
    event.stopPropagation();
    const isOwner = member?.role === "owner";
    const rawSubs = member?.subscriptions ?? member?.subscription;
    const hasSubscriptions = Array.isArray(rawSubs)
      ? rawSubs.length > 0
      : !!rawSubs && Object.keys(rawSubs).length > 0;
    const canActivate = member?.role === "member" || hasSubscriptions;

    const options = [
      canActivate && {
        id: "activate-subscription",
        label: "Ativar assinatura",
        icon: <AutoAwesomeRounded fontSize="small" />,
        onClick: () => onActivateSubscription?.(member),
      },
      canActivate && !isOwner && { divider: true },
      !isOwner && {
        id: "remove-member",
        label: "Remover membro",
        icon: <PersonRemoveRounded fontSize="small" />,
        variant: "danger",
        onClick: () => onRemoveMember?.(member),
      },
    ].filter(Boolean);

    rowMenuPopperRef.current?.open(options, "bottom-end", event.currentTarget);
  }

  const columns = [
    {
      id: "member",
      title: "Membro",
      minWidth: 240,
      allowReorder: false,
      customRender: (item) => <MemberCell member={item} />,
    },
    {
      id: "role",
      title: "Permissão",
      minWidth: 140,
      align: "center",
      customRender: (item) => (
        <RoleCell member={item} onChangeRole={onChangeRole} />
      ),
    },
    {
      id: "subscription",
      title: "Assinatura",
      minWidth: 200,
      grow: 2,
      align: "center",
      customRender: (item) => <SubscriptionCell member={item} plans={plans} />,
    },
    {
      id: "joined",
      title: "Membro desde",
      minWidth: 140,
      align: "left",
      customRender: (item) => <MemberSinceCell member={item} />,
    },
  ];

  return (
    <ModalComponent
      variant="search"
      maxWidth={1180}
      open={open}
      handleClose={onClose}
      loading={loading}
      onSearch={handleSearch}
      searchPlaceholder="Buscar membro por usuário ou apelido"
      subHeader={
        <FiltersBar
          filters={filters}
          plans={plans}
          plansLoading={plansLoading}
          onToggle={toggleFilterValue}
          onClear={clearFilters}
        />
      }
      bodyComponent={
        <div className="members-list-table-wrapper">
          <TableToolbar
            records={records}
            loading={loading}
            communityId={communityId}
            onExport={onExport}
          />
          <Table
            id="members-list"
            columns={columns}
            data={members}
            loading={loading}
            loadingRows={6}
            draggable={false}
            fullWidth
            emptyMessage="Nenhum membro encontrado"
            emptySubtitle="Ajuste os filtros ou tente uma nova busca"
            enablePagination
            page={page}
            pages={pages}
            records={records}
            onSelectPage={handlePageChange}
            onRowClick={handleViewMember}
            actionsComponent={(item) => (
              <RowActions
                member={item}
                onView={handleViewMember}
                onOpenMenu={openRowMenu}
              />
            )}
          />
          <Popper controlRef={rowMenuPopperRef} />
        </div>
      }
    />
  );
}
